﻿/*
 * Purpose: Compose phone number input with country selector and validation label.
 * Boundary: Component; owns hook state and delegates UI; no external data fetching.
 * Invariants:
 * - Input disabled until a country is selected.
 * - Validation matches selected country number format.
 * - Exposes isValid and details via context setters.
 * - ASCII only; no Unicode in comments or strings.
 */

// Imports
import { useContext, createContext, useEffect } from 'react';

import PhoneNumberLayout from './phonenumber/phonenumber-layout';

import { usePhoneNumber } from './phonenumber/phonenumber-hooks';

export const PhoneContext = createContext();

// Component PhoneNumber:
// Connects usePhoneNumber state to layout; updates parent via context
const PhoneNumber = ({
    enableCountrySelector = true,
    enabledCountries,
    defaultCountry = 'ZA',
    showValidationLabel = true,
    initialValue = '',
}) => {
    // State Management - initialize hook with selection and validation logic
    const props = usePhoneNumber({ 
        enableCountrySelector, 
        enabledCountries, 
        defaultCountry,
        initialValue 
    });

    // Context - get setters from parent
    const { setIsValid, setFullNumber, setCountryCode } = useContext(PhoneContext) || {};

    // Effect: Update parent via context
    useEffect(() => {
        if (props.selectedCountry && setFullNumber && setIsValid) {
            const fullNumber = `${props.selectedCountry.dialCode.replace('+', '')}${props.phoneNumber}`;
            setFullNumber(fullNumber);
            setIsValid(props.isValid);
            if (setCountryCode) setCountryCode(props.selectedCountry.code);
        }
    }, [props.selectedCountry, props.phoneNumber, props.isValid, setFullNumber, setIsValid, setCountryCode]);

    // Rendering - delegate UI and props to layout
    return (
        <PhoneNumberLayout
            enableCountrySelector={enableCountrySelector}
            showValidationLabel={showValidationLabel}
            {...props}
        />
    );
};

// Component metadata - support React DevTools naming
PhoneNumber.displayName = 'PhoneNumber';

// Export - default for use within registration flow
export default PhoneNumber;